import { legacyLocalLabel } from '../../../../utilities/legacyDatePresentation';
import { useCallback } from "react";
import React, { useEffect, useMemo, useState } from "react";
import { Download, Info, RefreshCw } from "lucide-react";
import { Spinner } from "react-bootstrap";
import { useAbortManager } from "../../../../hooks/cancelletionManagerHook/useAbortManager";
import { useAuth } from "../../../../firebase/AuthProvider";
import { GetPaymnetHistoryByOrgAsync } from "../../../../services/PaymentServices/OrgPaymentMethodService";
import { GetPaymnetHistoryByOrgRes, GetPaymnetHistoryReq } from "../../../../interfaces";
import { PaginationDto } from "../../../../interfaces/Employee/Employee";
import Pagination from "../../../ui/Pagination/Pagination";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { GetInvoiceByInvoiceNumberAsync } from "../../../../services/PaymentServices/HelimCustomerService";
import RefundModal from "./RefundModal";
import { HelcimPaymentStatus } from "../../../../Data/SD";

interface BillingHistoryProps {
    orgId?: number | undefined,
    isSuperAdmin: boolean | undefined
}

const BillingHistory: React.FC<BillingHistoryProps> = ({
    orgId,
    isSuperAdmin
}) => {
    const cancelletionManager = useAbortManager();
    const { currentUser } = useAuth();

    const [paymentHistory, setPaymentHistory] = useState<GetPaymnetHistoryByOrgRes[]>([]);
    const [paymentHistoryCount, setPaymentHistoryCount] = useState<number>(0);
    const [pagination, setPagination] = useState<PaginationDto>({ pageNumber: 1, pageSize: 10 });
    const [selectedRefundItem, setSelectedRefundItem] = useState<GetPaymnetHistoryByOrgRes | null>(null);
    const [selectedRefundMaxAmount, setSelectedRefundMaxAmount] = useState<number>(0);
    const [isRefundModalOpen, setIsRefundModalOpen] = useState<boolean>(false);
    const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);

    const getStatusMeta = (status?: string) => {
        const normalized = (status || "").trim().toLowerCase();
        if (!normalized) return { label: "Unknown", className: "bh_unknown" };
        if (normalized === "internal_deduction") return { label: "Internal Deduction", className: "bh_deduction" };
        if (normalized.includes("paid") || normalized.includes("success")) return { label: "Paid", className: "bh_paid" };
        if (normalized.includes("failed") || normalized.includes("error")) return { label: "Failed", className: "bh_failed" };
        if (normalized.includes("pending") || normalized.includes("processing")) return { label: "Pending", className: "bh_pending" };
        if (normalized.includes("partial")) return { label: "Partial Refund", className: "bh_partial" };
        if (normalized.includes("refund")) return { label: "Refunded", className: "bh_refunded" };
        return { label: status, className: "bh_unknown" };
    };

    const getTransactionType = (inv: GetPaymnetHistoryByOrgRes) => {
        if (inv.billingStatus?.toUpperCase() === "INTERNAL_DEDUCTION") return "Internal Deduction";
        if (inv.amount < 0) return "Refund";
        return "Payment";
    };

    const getTransactionStyle = (type: string) => {
        if (type === "Payment") return "text-emerald-600";
        if (type === "Refund") return "text-rose-600";
        if (type === "Internal Deduction") return "text-amber-500";
        return "text-gray-600";
    };

    const getAmountLabel = (type: string) => {
        if (type === "Refund") return "(Refund)";
        if (type === "Internal Deduction") return "(Credit Used)";
        return "";
    };

    const canRefundPayment = (item: GetPaymnetHistoryByOrgRes, group: { netAmount: number }) => {
        const status = item.billingStatus?.toUpperCase();
        const isSuccessOrPartial = status === HelcimPaymentStatus.SUCCESS || status === HelcimPaymentStatus.PARTIALLY_REFUNDED;
        return isSuccessOrPartial && item.amount > 0 && group.netAmount > 0;
    };

    const groupedPaymentHistory = useMemo(() => {
        const groups = paymentHistory.reduce((acc, inv) => {
            const invoiceKey = inv.paymentInvoiceNumber || "no-invoice";
            if (!acc[invoiceKey]) {
                acc[invoiceKey] = {
                    invoiceKey,
                    invoiceNumber: inv.paymentInvoiceNumber,
                    items: [] as GetPaymnetHistoryByOrgRes[],
                    totalPayments: 0,
                    totalRefunds: 0,
                    totalInternalDeductions: 0,
                };
            }

            const transactionType = getTransactionType(inv);
            acc[invoiceKey].items.push(inv);

            if (transactionType === "Payment") {
                acc[invoiceKey].totalPayments += inv.amount;
            } else if (transactionType === "Refund") {
                acc[invoiceKey].totalRefunds += Math.abs(inv.amount);
            } else if (transactionType === "Internal Deduction") {
                acc[invoiceKey].totalInternalDeductions += Math.abs(inv.amount);
            }

            return acc;
        }, {} as Record<string, {
            invoiceKey: string;
            invoiceNumber?: string | null;
            items: GetPaymnetHistoryByOrgRes[];
            totalPayments: number;
            totalRefunds: number;
            totalInternalDeductions: number;
        }>);

        return Object.values(groups).map((group) => ({
            ...group,
            netAmount: Number((group.totalPayments - group.totalRefunds - group.totalInternalDeductions).toFixed(2)),
        }));
    }, [paymentHistory]);

    // getStatusMeta, getTransactionType, getTransactionStyle, getAmountLabel are defined above useMemo already
    // and are used by render logic below.

    const getBillinghistory = useCallback(async () => {
        if (!currentUser) return;
        const token = await currentUser.getIdToken();
        try {
            const payload: GetPaymnetHistoryReq = {
                pageNumber: pagination.pageNumber,
                pageSize: pagination.pageSize,
                sortBy: null,
                sortDirection: null,
                organizationId: orgId || null
            }
            const single = cancelletionManager.create("getbilling");
            const billingHistoryRes = await GetPaymnetHistoryByOrgAsync(token, payload, single.signal);
            if (billingHistoryRes.success) {
                setPaymentHistory(billingHistoryRes.data.items);
                setPaymentHistoryCount(billingHistoryRes.data.totalCount);
            }
        } catch (error: any) {
            if (error.name === 'AbortError') return;
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to get the data");
        } finally {
            cancelletionManager.remove("getbilling");
        }
    }, [currentUser, orgId, pagination, cancelletionManager])

useEffect(() => {
        getBillinghistory();
    }, [pagination, getBillinghistory]);



    const onPageChange = (pageNum: number) => {
        setPagination((prev) => ({
            ...prev,
            pageNumber: pageNum
        }))
    }

    const resetPageSize = (pageNumber: number, pageSize: number) => {
        setPagination({
            pageNumber: pageNumber,
            pageSize: pageSize
        })
    }

    const openRefundModal = (item: GetPaymnetHistoryByOrgRes, maxAmount: number) => {
        setSelectedRefundItem(item);
        setSelectedRefundMaxAmount(maxAmount);
        setIsRefundModalOpen(true);
    };

    const closeRefundModal = () => {
        setIsRefundModalOpen(false);
        setSelectedRefundItem(null);
        setSelectedRefundMaxAmount(0);
    };

    const handleRefundSuccess = () => {
        closeRefundModal();
        getBillinghistory();
    };

    const downloadInvoice = async (invoiceNumber: string) => {
        if (!currentUser) return;
        setDownloadingInvoiceId(invoiceNumber);

        const token = await currentUser?.getIdToken();
        try {
            const controller = cancelletionManager.create(`download-${invoiceNumber}`);
            const res = await GetInvoiceByInvoiceNumberAsync(token, invoiceNumber, controller.signal);
            if (res.success && res.data) {
                window.open(res.data, "_blank"); // opens invoice in new tab
            }

        } catch (error: any) {
            if (error.name === 'AbortError') return;
            toast.error(error instanceof AxiosError ? error.response?.data.message : error.message || "Error to get the data");
        } finally {
            cancelletionManager.remove(`download-${invoiceNumber}`);
            setDownloadingInvoiceId(null);
        }
    }

    return (
        <div className="bh_container">
            <div className="flex items-center justify-between mb-4">
                <h3 className="bh_title mb-0">Billing History</h3>
                <div className="group relative">
                    <Info size={16} className="text-blue-500 cursor-help" aria-label="Billing information tooltip" />
                    <div className="absolute bottom-full right-0 mb-2 hidden group-hover:block bg-gray-800 text-white text-xs px-2 py-1 rounded shadow-lg whitespace-nowrap transition-opacity duration-200">
                        Refunds may take up to 1 business day to settle. Invoice totals may update after settlement.
                    </div>
                </div>
            </div>

            <table className="bh_table">
                <thead>
                    <tr>
                        <th>Start Date</th>
                        <th>End Date</th>
                        <th>Type</th>
                        <th>Invoice</th>
                        <th>Charge Id</th>
                        <th>Amount</th>                        <th>Net Amount</th>                        <th>Status</th>
                        <th>Action</th>
                    </tr>
                </thead>

                <tbody>
                    {paymentHistory.length === 0 ? (
                        <tr>
                            <td colSpan={9} className="bh_no_data">
                                No billing history found.
                            </td>
                        </tr>
                    ) : (
                        groupedPaymentHistory.map((group) => (
                            <React.Fragment key={group.invoiceKey}>
                                {group.items.map((inv, index) => {
                                    const txType = getTransactionType(inv);
                                    const showNetAmount = txType === "Payment" || index === 0;
                                    const netStyle = group.netAmount > 0 ? "text-emerald-600" : "text-rose-600";
                                    return (
                                        <tr key={inv.paymentId}>
                                            <td>{inv.billingStartDate ? legacyLocalLabel(inv.billingStartDate, "MMM dd, yyyy") : "-"}</td>
                                            <td>{inv.billingEndDate ? legacyLocalLabel(inv.billingEndDate, "MMM dd, yyyy") : "-"}</td>

                                            <td>
                                                {(() => {
                                                    const txType = getTransactionType(inv);
                                                    return (
                                                        <span className={`${getTransactionStyle(txType)} font-semibold`}>
                                                            {txType}
                                                        </span>
                                                    );
                                                })()}
                                            </td>

                                            <td>
                                                {inv.paymentInvoiceNumber ? (
                                                    <span className="text-sky-700">#{inv.paymentInvoiceNumber}</span>
                                                ) : (
                                                    <span className="text-slate-400">N/A</span>
                                                )}
                                            </td>

                                            <td title={inv.paymentId} className="text-sm text-gray-600">
                                                {inv.paymentId ? `${inv.paymentId.slice(0, 8)}...` : "-"}
                                            </td>

                                            <td>
                                                {(() => {
                                                    const txType = getTransactionType(inv);
                                                    const amountAbs = Math.abs(inv.amount).toFixed(2);
                                                    const label = getAmountLabel(txType);
                                                    const style = txType === "Payment" ? "text-emerald-600" : txType === "Refund" ? "text-rose-600" : "text-amber-500";
                                                    return (
                                                        <div className={`${style} font-semibold`}>
                                                            ${amountAbs} {label}
                                                            {(txType === "Refund" || txType === "Internal Deduction") && inv.paymentInvoiceNumber && (
                                                                <div className={`text-xs ${txType === "Refund" ? "text-rose-500" : "text-amber-500"}`}>
                                                                    {txType === "Refund" ? `Refund for Invoice #${inv.paymentInvoiceNumber}` : `Internal credit used for Invoice #${inv.paymentInvoiceNumber}`}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })()}
                                            </td>

                                            <td className={`font-semibold ${netStyle}`}>
                                                {showNetAmount ? `$${group.netAmount.toFixed(2)}` : "-"}
                                            </td>

                                            <td>
                                                {(() => {
                                                    const statusMeta = getStatusMeta(inv.billingStatus);
                                                    return (
                                                        <span className={`bh_status ${statusMeta.className}`}>
                                                            {statusMeta.label}
                                                        </span>
                                                    );
                                                })()}
                                            </td>

                                            <td>
                                                <div className="flex items-center gap-2">
                                                    <button
                                                        className="flex items-center justify-center p-2 rounded-md bg-slate-100 hover:bg-slate-200 text-slate-700"
                                                        onClick={() => downloadInvoice(inv.paymentInvoiceNumber)}
                                                        title="Download Invoice"
                                                        disabled={!inv.paymentInvoiceNumber || (downloadingInvoiceId !== null && downloadingInvoiceId !== inv.paymentInvoiceNumber)}
                                                    >
                                                        {downloadingInvoiceId === inv.paymentInvoiceNumber ? (
                                                            <Spinner animation="border" size="sm" />
                                                        ) : (
                                                            <Download size={18} />
                                                        )}
                                                    </button>

                                                    {isSuperAdmin && <button
                                                        aria-label="Refund"
                                                        title="Refund"
                                                        disabled={!canRefundPayment(inv, group)}
                                                        onClick={() => openRefundModal(inv, Math.min(group.netAmount, inv.amount))}
                                                        className="flex items-center justify-center p-2 bg-[#7E57C2] hover:bg-[#6A4AA8] text-white rounded-lg text-[0.85rem] font-medium transition-colors shadow-sm disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed disabled:shadow-none"
                                                    >
                                                        <RefreshCw size={16} />
                                                    </button>}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </React.Fragment>
                        ))
                    )}
                </tbody>
            </table>

            {(paymentHistoryCount && paymentHistoryCount > 0) ? (
                <Pagination
                    currentPage={pagination.pageNumber}
                    totalPages={Math.ceil(paymentHistoryCount / pagination.pageSize)}
                    onPageChange={onPageChange}
                    pageSize={pagination.pageSize}
                    resetPageSize={resetPageSize}
                />
            ) : <></>}

            {isRefundModalOpen &&
                <RefundModal
                    isOpen={isRefundModalOpen}
                    onClose={closeRefundModal}
                    onSuccess={handleRefundSuccess}
                    chargeId={selectedRefundItem?.paymentId ?? ''}
                    maxAmount={selectedRefundMaxAmount}
                />}
        </div>
    );
};

export default BillingHistory;